import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { twofaAPI } from '../services/api';

const accent = '#00D4AA';
const cardBg = 'rgba(255,255,255,0.03)';
const textMuted = '#94a3b8';
const danger = '#ef4444';

export default function BedrijfMfaSection() {
  const { gebruiker: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // null | 'enable' | 'disable'
  const [success, setSuccess] = useState('');
  const [rolKeuze, setRolKeuze] = useState(null);
  const [savingRollen, setSavingRollen] = useState(false);
  const [rollenSaved, setRollenSaved] = useState(false);

  // Alleen tonen voor bedrijf_admin of super_admin
  const isBedrijfAdmin = user && user.rol === 'bedrijf_admin';
  const isSuperAdmin = user && user.rol === 'super_admin';
  const canManage = isBedrijfAdmin || isSuperAdmin;

  useEffect(() => {
    if (!canManage || !user.bedrijf_id) {
      setLoading(false);
      return;
    }
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setLoading(true);
      setError('');
      const res = await twofaAPI.getBedrijfMfa(user.bedrijf_id);
      setData(res.data);
      const _alle = (res.data.rollen || []).map((r) => r.id);
      const _verpl = res.data.bedrijf.mfa_required_rollen;
      setRolKeuze(new Set(_verpl && _verpl.length ? _verpl : _alle));
    } catch (err) {
      setError(err?.response?.data?.message || 'Kon bedrijf-MFA status niet laden');
    } finally {
      setLoading(false);
    }
  }

  async function doToggle(required) {
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      const res = await twofaAPI.setBedrijfMfa(user.bedrijf_id, required);
      setData({ bedrijf: res.data.bedrijf, stats: res.data.stats });
      setSuccess(res.data.message);
      setConfirm(null);
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Toggle mislukt');
    } finally {
      setBusy(false);
    }
  }

  function toggleRol(id) {
    setRolKeuze((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function doSaveRollen() {
    try {
      setSavingRollen(true);
      setError('');
      const alle = (data.rollen || []).map((r) => r.id);
      const gekozen = [...(rolKeuze || [])];
      const payload = (alle.length > 0 && gekozen.length === alle.length) ? null : gekozen;
      await twofaAPI.setBedrijfMfaRollen(user.bedrijf_id, payload);
      setRollenSaved(true);
      setTimeout(() => setRollenSaved(false), 4000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Opslaan rollen mislukt');
    } finally {
      setSavingRollen(false);
    }
  }

  if (!canManage) return null;

  if (loading) {
    return (
      <div style={{ marginTop: '32px', padding: '20px', background: cardBg, borderRadius: '12px', border: '1px solid rgba(0,212,170,0.15)' }}>
        <p style={{ color: textMuted, margin: 0 }}>Bedrijf-MFA status laden…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ marginTop: '32px', padding: '20px', background: cardBg, borderRadius: '12px', border: `1px solid ${danger}` }}>
        <p style={{ color: danger, margin: 0 }}>⚠️ {error}</p>
        <button type="button" onClick={loadStatus} style={{ marginTop: '12px', padding: '8px 16px', background: 'transparent', color: accent, border: `1px solid ${accent}`, borderRadius: '8px', cursor: 'pointer' }}>
          Opnieuw proberen
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { bedrijf, stats } = data;
  const enabled = bedrijf.mfa_required;

  return (
    <div style={{ marginTop: '32px', padding: '24px', background: cardBg, borderRadius: '12px', border: '1px solid rgba(0,212,170,0.15)' }}>
      <h3 style={{ color: accent, marginTop: 0, marginBottom: '8px', fontSize: '18px' }}>
        🏢 Organisatie-beveiliging — {bedrijf.naam}
      </h3>
      <p style={{ color: textMuted, marginTop: 0, marginBottom: '20px', fontSize: '14px' }}>
        Wanneer ingeschakeld, moeten alle gebruikers van {bedrijf.naam} 2FA opzetten bij hun volgende login.
      </p>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <span style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: 'bold',
          background: enabled ? 'rgba(0,212,170,0.15)' : 'rgba(148,163,184,0.15)',
          color: enabled ? accent : textMuted,
          border: `1px solid ${enabled ? accent : textMuted}`
        }}>
          {enabled ? '✓ 2FA VERPLICHT' : '○ 2FA optioneel'}
        </span>
        {enabled && bedrijf.mfa_required_sinds && (
          <span style={{ color: textMuted, fontSize: '13px' }}>
            sinds {new Date(bedrijf.mfa_required_sinds).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
            {bedrijf.mfa_required_door && ` door ${bedrijf.mfa_required_door.naam || bedrijf.mfa_required_door.gebruikersnaam}`}
          </span>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e2e8f0' }}>{stats.totaal_actieve_gebruikers}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Actieve gebruikers</div>
        </div>
        <div style={{ padding: '12px', background: 'rgba(0,212,170,0.08)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: accent }}>{stats.met_2fa}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Met 2FA actief</div>
        </div>
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.08)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats.zonder_2fa > 0 ? danger : textMuted }}>{stats.zonder_2fa}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Zonder 2FA</div>
        </div>
      </div>

      {/* Per-rol 2FA-plicht */}
      {enabled && data.rollen && data.rollen.length > 0 && (
        <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px' }}>
          <p style={{ color: '#e2e8f0', margin: 0, marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
            Voor welke rollen is 2FA verplicht?
          </p>
          <p style={{ color: textMuted, margin: 0, marginBottom: '12px', fontSize: '12px' }}>
            Alles aangevinkt = iedereen. Vink rollen uit om de plicht te beperken.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
            {data.rollen.map((r) => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#e2e8f0' }}>
                <input type="checkbox" checked={rolKeuze ? rolKeuze.has(r.id) : false} onChange={() => toggleRol(r.id)} />
                {r.naam}
              </label>
            ))}
          </div>
          <button type="button" onClick={doSaveRollen} disabled={savingRollen} style={{ padding: '8px 16px', background: accent, color: '#0a0e1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
            {savingRollen ? 'Opslaan…' : rollenSaved ? '✓ Opgeslagen' : 'Rollen opslaan'}
          </button>
        </div>
      )}

      {/* Success / Error */}
      {success && (
        <div style={{ padding: '12px', background: 'rgba(0,212,170,0.1)', border: `1px solid ${accent}`, borderRadius: '8px', color: accent, marginBottom: '16px', fontSize: '13px' }}>
          ✓ {success}
        </div>
      )}
      {error && (
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.1)', border: `1px solid ${danger}`, borderRadius: '8px', color: danger, marginBottom: '16px', fontSize: '13px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Confirmation dialog */}
      {confirm === 'enable' && (
        <div style={{ padding: '16px', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ color: '#f59e0b', margin: 0, marginBottom: '8px', fontWeight: 'bold' }}>⚠️ Weet je het zeker?</p>
          <p style={{ color: '#e2e8f0', margin: 0, marginBottom: '12px', fontSize: '14px' }}>
            {stats.zonder_2fa} van {stats.totaal_actieve_gebruikers} gebruikers in {bedrijf.naam} moet 2FA opzetten bij de volgende login. Tot dat moment kunnen zij niet inloggen via de app.
            {' '}<strong>Zorg dat de monteurs-app v2+ uitgerold is</strong>, anders verliezen monteurs hun mobiele toegang.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => doToggle(true)} disabled={busy} style={{ padding: '8px 16px', background: '#f59e0b', color: '#0a0e1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              {busy ? 'Bezig…' : 'Ja, 2FA verplicht maken'}
            </button>
            <button type="button" onClick={() => setConfirm(null)} disabled={busy} style={{ padding: '8px 16px', background: 'transparent', color: textMuted, border: `1px solid ${textMuted}`, borderRadius: '8px', cursor: 'pointer' }}>
              Annuleren
            </button>
          </div>
        </div>
      )}

      {confirm === 'disable' && (
        <div style={{ padding: '16px', background: 'rgba(148,163,184,0.1)', border: `1px solid ${textMuted}`, borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ color: '#e2e8f0', margin: 0, marginBottom: '12px', fontSize: '14px' }}>
            2FA-verplichting verwijderen voor {bedrijf.naam}? Gebruikers die al 2FA hebben, behouden hun instellingen.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => doToggle(false)} disabled={busy} style={{ padding: '8px 16px', background: textMuted, color: '#0a0e1a', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              {busy ? 'Bezig…' : 'Ja, uitschakelen'}
            </button>
            <button type="button" onClick={() => setConfirm(null)} disabled={busy} style={{ padding: '8px 16px', background: 'transparent', color: textMuted, border: `1px solid ${textMuted}`, borderRadius: '8px', cursor: 'pointer' }}>
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!confirm && (
        <div>
          {enabled ? (
            <button type="button" onClick={() => setConfirm('disable')} style={{ padding: '10px 20px', background: 'transparent', color: textMuted, border: `1px solid ${textMuted}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              Verplichting uitschakelen
            </button>
          ) : (
            <button type="button" onClick={() => setConfirm('enable')} style={{ padding: '10px 20px', background: accent, color: '#0a0e1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              🔐 2FA verplicht maken voor {bedrijf.naam}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
